import { industries, cta, brand } from '../content';
import { usePageMeta, Swipe, Button, Arrow, SmartLink, cx } from '../ui';
import { SectorVignette } from './industries/Vignettes';
import { sectorPages } from './industries/data';

// Bento order: alternate wide/narrow so the grid never reads as a uniform card wall.
const SPANS = ['md:col-span-7', 'md:col-span-5', 'md:col-span-5', 'md:col-span-7', 'md:col-span-7', 'md:col-span-5', 'md:col-span-5', 'md:col-span-7'];

const THREAD = [
  { t: 'Someone stands between you and the owner', d: 'A dealer, installer, hirer or retailer. We keep them in the record.' },
  { t: 'The product outlives the sale', d: 'Decades in service, often with more than one owner.' },
  { t: 'The serial number is what matters', d: 'Which build, which manual, which part, which inspection.' },
];

export default function Industries() {
  usePageMeta(
    'Industries: warranty, dealers and service records by sector',
    'BrandedMark for agricultural machinery, lifting and access, heating and heat pumps, commercial kitchen, precision manufacturing, premium durables, medical and laboratory instruments, and sports and fitness equipment.'
  );

  return (
    <div className="ind-overview">
      {/* Hero */}
      <section className="wrap pb-12 pt-16 sm:pt-24">
        <div className="grid gap-10 lg:grid-cols-[1.25fr_1fr] lg:items-end">
          <div>
            <h1 className="t-h1 max-w-[15ch] [text-wrap:balance]">
              Built for things that <Swipe draw>last.</Swipe>
            </h1>
          </div>
          <div className="lg:pb-2">
            <p className="t-lede max-w-[46ch]">
              For manufacturers who sell through dealers, installers, hirers and retailers.
            </p>
          </div>
        </div>
      </section>

      {/* Tiles */}
      <section className="wrap pb-24">
        <div className="grid gap-5 md:grid-cols-12">
          {industries.map((ind, i) => {
            const page = sectorPages[ind.slug];
            return (
              <SmartLink
                key={ind.slug}
                href={`/industries/${ind.slug}`}
                className={cx('ind-tile group flex flex-col overflow-hidden rounded-[22px] bg-white ring-1 ring-bm-line', SPANS[i])}
              >
                <div className="relative h-[260px] overflow-hidden sm:h-[300px]">
                  <SectorVignette slug={ind.slug} />
                </div>
                <div className="flex flex-1 flex-col border-t border-bm-line p-6 sm:p-7">
                  {page && <span className="text-[13.5px] font-medium text-bm-emerald">Through the {page.channel}</span>}
                  <h2 className="t-h3 mt-2">{ind.name}</h2>
                  <p className="mt-2 text-[15.5px] text-bm-muted">{ind.short}.</p>
                  <span className="ind-link mt-auto inline-flex items-center gap-2 pt-6 text-bm-indigo">
                    <span className="transition-transform duration-200 group-hover:translate-x-1">
                      <Arrow />
                    </span>
                  </span>
                </div>
              </SmartLink>
            );
          })}
        </div>
      </section>

      {/* Common thread */}
      <section className="bg-bm-alt">
        <div className="wrap section">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <span className="bar" />
              <h2 className="t-h2 max-w-[13ch]">
                What every sector has in <Swipe>common.</Swipe>
              </h2>
              <p className="t-lede mt-5 max-w-[38ch]">{brand.belief}</p>
              <div className="mt-10 border-t border-bm-line pt-6">
                <p className="max-w-[40ch] text-[16px] text-bm-ink">
                  <span className="font-semibold text-bm-indigo">Make something else?</span> If it has a serial number and a long life, we should talk.
                </p>
                <Button href={`${cta.primaryHref}?src=industries`} className="mt-5">
                  {cta.primary} <Arrow />
                </Button>
              </div>
            </div>
            <ol className="divide-y divide-bm-line border-y border-bm-line">
              {THREAD.map((t, i) => (
                <li key={t.t} className="grid grid-cols-[48px_1fr] gap-4 py-7">
                  <span className="text-[15px] font-semibold text-bm-emerald">{i + 1}</span>
                  <div>
                    <h3 className="text-[19px] font-bold tracking-[-0.015em] text-bm-indigo">{t.t}</h3>
                    <p className="mt-1.5 text-[16px] text-bm-muted">{t.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <style>{`
        .v2 .ind-tile { transition: box-shadow .2s var(--bm-ease), transform .2s var(--bm-ease); }
        .v2 .ind-tile:hover { box-shadow: 0 30px 60px -34px rgba(24,19,67,.4), 0 0 0 1px #241E61; }
        .v2 .ind-tile img { transition: transform .6s var(--bm-ease); }
        .v2 .ind-tile:hover img { transform: scale(1.025); }
        .v2 .ind-tile:hover .ind-link { color: var(--bm-emerald); }
      `}</style>
    </div>
  );
}
